import { IDestroyOptions, Rectangle, Sprite } from "pixi.js";
import { BaseScalableView } from "../../../common/BaseScalableView";
import { ICanvasScaler } from "by/gamefactory/canvas/ICanvasScaler";
import { PromiseCompletionSource } from "by/gamefactory/shared/async/PromiseCompletionSource";
import { CommonResourcesPackage } from "../../../common/resources/CommonResourcesPackage";
import { FullscreenInputBlocker } from "../../../game/components/FullscreenInputBlocker";
import { gsap } from "gsap";
import { MyWalletResourcePackage } from "../resources/MyWalletResourcePackage";
import { Web3Model } from "by/gamefactory/web3/models/Web3Model";
import { IMyWalletController } from "../controllers/IMyWalletController";
import { ILogger } from "by/gamefactory/cityversetycoon/logger/ILogger";
import { EmitterConfigV2 } from "@pixi/particle-emitter";
import { MyWalletParticleView } from "../components/particles/MyWalletParticleView";
import { ButtonWithScaleAnimation } from "../../../common/components/ButtonWithScaleAnimation";
import { MyWalletsBox } from "../components/wallets/MyWalletsBox";
import { MyWalletsBoxElement } from "../components/wallets/MyWalletsBoxElement";
import { ScrollPage } from "./pages/ScrollPage";
import { WalletPage } from "./pages/WalletPage";
import { MyWalletsBoxElementType } from "../components/wallets/MyWalletsBoxElementType";
import { MyWalletPage } from "./pages/MyWalletPage";
import { IMyWalletModel } from "../model/IMyWalletModel";
import { nameof } from "../../../../../shared/bindable-data";
import { MyWalletInfoTextComponent } from "../components/MyWalletInfoTextComponent";
import { WalletTabType } from "../components/WalletTabType";
import { WalletsClickableArea } from "../components/wallets/WalletsClickableArea";
import { NFTBuilding } from "by/gamefactory/web3/nfts/NFTBuilding";
import { WalletFullBuildingContainer } from "../components/fullBuilding/WalletFullBuildingContainer";
import { UserProfileResourcePackage } from "../../userprofile/resources/UserProfileResourcePackage";
import { IGuestModel } from "../../../common/models/IGuestModel";
import { DynamicAssets } from "../../../../../resources/DynamicAssets";
import { AsyncOperationNotification } from "../../../common/components/AsyncOperationNotification";
import { IInventory } from "../../../../../web3/models/IInventory";
import { IPlayerModel } from "../../../common/models/IPlayerModel";
import { Web3Config } from "../../../../../web3/config/Web3Config";
import { GameInfo } from "../../../../../analytics/GameInfo";

export class MyWalletPopupView extends BaseScalableView<void> {
    private static readonly ANIMATION_DURATION = 0.5;

    private readonly commonResources: CommonResourcesPackage;
    private readonly myWalletResources: MyWalletResourcePackage;
    private readonly buildingResources: DynamicAssets;
    private readonly piggyClubResources: DynamicAssets;
    private readonly userProfileResources: UserProfileResourcePackage;
    private readonly web3Model: Web3Model;
    private readonly web3Config: Web3Config;
    private readonly myWalletModel: IMyWalletModel;
    private readonly myWalletController: IMyWalletController;
    private readonly logger: ILogger;
    private readonly animations: gsap.core.Timeline;
    private readonly inventory: IInventory;
    private readonly forceTab: WalletTabType;
    private readonly guestModel: IGuestModel;
    private readonly asyncOperationNotification: AsyncOperationNotification;
    private readonly playerModel: IPlayerModel;
    private readonly gameInfo: GameInfo;

    private readonly externalWallets: Array<MyWalletsBoxElement>;
    private mainWallet: MyWalletsBoxElement;
    private connectWallet: MyWalletsBoxElement;

    private viewBackground: Sprite;
    private leftLight: Sprite;
    private rightLight: Sprite;
    private header: Sprite;
    private footer: Sprite;
    private myWalletsBox: MyWalletsBox;
    private walletsClickableArea: WalletsClickableArea;
    private largeParticles: MyWalletParticleView;
    private smallParticles: MyWalletParticleView;
    private closeButton: ButtonWithScaleAnimation;
    private infoText: MyWalletInfoTextComponent;
    private fullBuildingContainer: WalletFullBuildingContainer;

    private scrollPage: ScrollPage;
    private walletPage: WalletPage;
    private currentPageValue: MyWalletPage;

    private set currentPage(page: MyWalletPage) {
        if (this.currentPageValue === page) {
            return;
        }
        if (this.currentPageValue) {
            this.currentPageValue.hide();
            this.currentPageValue.eventMode = 'none';
        }
        this.currentPageValue = page;
        this.currentPageValue.eventMode = 'static';
        this.currentPageValue.show();
    }

    private get currentPage(): MyWalletPage {
        return this.currentPageValue;
    }

    public constructor(
        operationControls: PromiseCompletionSource<void>,
        canvasScaler: ICanvasScaler,
        commonResources: CommonResourcesPackage,
        myWalletResources: MyWalletResourcePackage,
        buildingResources: DynamicAssets,
        piggyClubResources: DynamicAssets,
        userProfileResources: UserProfileResourcePackage,
        web3Model: Web3Model,
        myWalletModel: IMyWalletModel,
        web3Config: Web3Config,
        myWalletController: IMyWalletController,
        inventory: IInventory,
        logger: ILogger,
        guestModel: IGuestModel,
        forceTab: WalletTabType,
        asyncOperationNotification: AsyncOperationNotification,
        playerModel: IPlayerModel,
        gameInfo: GameInfo
    ) {
        super(operationControls, canvasScaler);
        this.commonResources = commonResources;
        this.myWalletResources = myWalletResources;
        this.buildingResources = buildingResources;
        this.piggyClubResources = piggyClubResources;
        this.userProfileResources = userProfileResources;
        this.web3Model = web3Model;
        this.web3Config = web3Config;
        this.myWalletModel = myWalletModel;
        this.myWalletController = myWalletController;
        this.inventory = inventory;
        this.logger = logger;
        this.guestModel = guestModel;
        this.forceTab = forceTab;
        this.asyncOperationNotification = asyncOperationNotification;
        this.playerModel = playerModel;
        this.gameInfo = gameInfo;
        this.animations = new gsap.core.Timeline({ paused: true });
        this.externalWallets = [];
        this.build();
        this.buildParticles();
        this.initializeWalletsBoxWallets();
        this.createWalletPage();
        this.createScrollPage();
        this.createFullBuilding();
        this.currentPage = this.scrollPage;
        this.layout(this.canvasScaler.referenceWidth, this.canvasScaler.referenceHeight);
        this.initAnimation();
        this.closeButton.onUp.add(this.onCloseButtonClickHandle, this);
        this.myWalletsBox.onWalletClick.add(this.onWalletClickHandle, this);
        this.myWalletModel.propertyChanged.add(this.onMyWalletModelPropertyChanged, this);
        this.web3Model.propertyChanged.add(this.onWeb3ModelPropertyChanged, this);
        this.walletPage.onCloseButtonClick.add(this.onWalletCloseButtonClickHandle, this);
        this.infoText.onContactClick.add(this.onInfoViewClickHandle, this);
        this.walletsClickableArea.onClick.add(this.myWalletsBox.showTooltip, this.myWalletsBox);
        this.scrollPage.onBuildingClick.add(this.showFullBuilding, this);
    }

    private build(): void {
        this.viewBackground = new Sprite(this.commonResources.assets_common_blured_backgrounds_blurry_lobby_jpg);
        this.viewBackground.eventMode = "static";
        this.leftLight = new Sprite(this.myWalletResources.assets_views_my_wallet_ui_vertical_light_png);
        this.rightLight = new Sprite(this.myWalletResources.assets_views_my_wallet_ui_vertical_light_png);
        this.rightLight.scale.x = -1;
        this.header = new Sprite(this.myWalletResources.assets_views_my_wallet_ui_wallet_header_png);
        this.myWalletsBox = new MyWalletsBox(this.commonResources, this.myWalletResources, this.logger);
        this.walletsClickableArea = new WalletsClickableArea();
        this.header.addChild(this.walletsClickableArea, this.myWalletsBox);
        this.footer = new Sprite(this.myWalletResources.assets_views_my_wallet_ui_wallet_footer_png);
        this.closeButton = new ButtonWithScaleAnimation(this.commonResources.assets_common_x_png);
        this.closeButton.hitArea = new Rectangle(-this.closeButton.width * 0.5, -this.closeButton.height * 0.5, this.closeButton.width * 2, this.closeButton.height * 2);
        this.infoText = new MyWalletInfoTextComponent();

        this.addChild(
            this.viewBackground,
            this.leftLight,
            this.rightLight,
            this.header,
            this.footer,
            this.closeButton,
            this.infoText
        );
    }

    private buildParticles(): void {
        const textures = [
            this.myWalletResources.assets_views_my_wallet_particles_3_2_png,
            this.myWalletResources.assets_views_my_wallet_particles_3_png,
            this.myWalletResources.assets_views_my_wallet_particles_4_2_png,
            this.myWalletResources.assets_views_my_wallet_particles_4_png,
            this.myWalletResources.assets_views_my_wallet_particles_particle_V2_png,
            this.myWalletResources.assets_views_my_wallet_particles_particle_png
        ];
        const particlesConfig1 = this.myWalletResources.assets_views_my_wallet_particles_emitter_V5_2_json as EmitterConfigV2;
        this.largeParticles = new MyWalletParticleView(particlesConfig1, textures);
        this.largeParticles.scale.set(1.4, 1.3);
        const particlesConfig2 = this.myWalletResources.assets_views_my_wallet_particles_emitter_V7_2_json as EmitterConfigV2;
        this.smallParticles = new MyWalletParticleView(particlesConfig2, textures);
        this.footer.addChild(this.largeParticles, this.smallParticles);
    }

    private initializeWalletsBoxWallets(): void {
        this.mainWallet = this.myWalletsBox.addWallet({ type: MyWalletsBoxElementType.THIRDWEB, walletAddress: this.web3Model.smartWalletAddress ?? this.web3Model.address.value, networkType: this.web3Model.chainName.value });
        for (const externalWallet of this.myWalletModel.wallets.value) {
            this.externalWallets.push(this.myWalletsBox.addWallet({
                type: externalWallet.type === "COINBASE" ? MyWalletsBoxElementType.COINBASE : MyWalletsBoxElementType.METAMASK,
                walletAddress: externalWallet.walletAddress
            }));
        }
        if (this.externalWallets.length < 4) {
            this.connectWallet = this.myWalletsBox.addWallet({ type: MyWalletsBoxElementType.CONNECT_WALLET });
        }
    }

    private async createScrollPage(): Promise<void> {
        const scrollPagePCS = new PromiseCompletionSource<void>();
        this.scrollPage = new ScrollPage(
            this.canvasScaler,
            scrollPagePCS,
            this.myWalletResources,
            this.commonResources,
            this.buildingResources,
            this.piggyClubResources,
            this.logger,
            this.inventory,
            this.web3Model,
            this.web3Config,
            this.myWalletController,
            this.forceTab,
            this.asyncOperationNotification,
            this.playerModel,
            this.gameInfo
        );
        this.addChild(this.scrollPage);
        await scrollPagePCS.result;
        await this.hide();
        this.operationControls.complete();
    }

    private async createWalletPage(): Promise<void> {
        const walletPagePCS = new PromiseCompletionSource<void>();
        this.walletPage = new WalletPage(
            this.canvasScaler,
            walletPagePCS,
            this.myWalletResources,
            this.commonResources,
            this.web3Model,
            this.myWalletController,
            this.myWalletsBox
        );
        this.currentPage = this.walletPage;
        this.addChild(this.walletPage);
        await walletPagePCS.result;
        await this.hide();
        this.operationControls.complete();
    }

    private createFullBuilding(): void {
        this.fullBuildingContainer = new WalletFullBuildingContainer(
            this.canvasScaler,
            this.commonResources,
            this.userProfileResources,
            this.guestModel,
            this.playerModel,
            this.buildingResources
        );
        this.addChild(this.fullBuildingContainer);
    }

    private layout(width: number, _height: number): void {
        this.leftLight.position.set(0, 381);
        this.rightLight.position.set(width, 381);
        this.header.position.set(0, 77);
        this.footer.position.set(0, 1231);
        this.largeParticles.position.set(245, 214);
        this.smallParticles.position.set(400, 192);
        this.closeButton.position.set(375, 1545);
        this.infoText.position.set(171, 1665);
        this.walletsClickableArea.position.set(123, 300);
        this.scrollPage.position.set(0, 590);
        this.walletPage.position.set(108, 563);
        this.layoutWalletsBox();
    }

    private layoutWalletsBox(): void {
        this.myWalletsBox.position.set((this.header.width - this.myWalletsBox.width) * 0.5 + 15, 353);
    }

    private initAnimation(): void {
        this.animations.fromTo(this.header.position, { y: this.header.position.y + 300 }, { y: this.header.position.y, ease: 'power1.out', duration: MyWalletPopupView.ANIMATION_DURATION }, '<');
        this.animations.fromTo(this.footer.position, { y: this.footer.position.y - 300 }, { y: this.footer.position.y, ease: 'power1.out', duration: MyWalletPopupView.ANIMATION_DURATION }, '<');
        this.animations.fromTo(this.myWalletsBox, { alpha: 0 }, { alpha: 1, ease: 'power1.in', duration: MyWalletPopupView.ANIMATION_DURATION }, '<');
        this.animations.fromTo(this.infoText, { alpha: 0 }, { alpha: 1, ease: 'power1.in', duration: MyWalletPopupView.ANIMATION_DURATION }, '<');
        this.animations.fromTo(this.largeParticles, { alpha: 0 }, { alpha: 1, ease: 'power1.in', duration: MyWalletPopupView.ANIMATION_DURATION }, '<');
        this.animations.fromTo(this.smallParticles, { alpha: 0 }, { alpha: 1, ease: 'power1.in', duration: MyWalletPopupView.ANIMATION_DURATION }, '<');
        this.animations.fromTo(this.closeButton, { alpha: 0 }, { alpha: 1, ease: 'power1.in', duration: MyWalletPopupView.ANIMATION_DURATION }, '<');
    }

    private async onWalletClickHandle(_wallet: MyWalletsBoxElement): Promise<void> {
        if (this.currentPage === this.walletPage) {
            return;
        }
        this.currentPage = this.walletPage;
    }

    private async onMyWalletModelPropertyChanged(model: IMyWalletModel, key: string) {
        switch (key) {
            case nameof(model, "wallets"):
                //TODO: that is hard update, can be smooth with putting wallet by index and update existed data dynamicly
                const oldExternalWalletsAddresses = this.externalWallets.flatMap((walletBoxElement) => walletBoxElement.data.walletAddress);
                if (this.connectWallet != null) {
                    this.myWalletsBox.removeWallet(this.connectWallet);
                }
                for (const externalWallet of this.externalWallets) {
                    this.myWalletsBox.removeWallet(externalWallet);
                }
                this.externalWallets.length = 0;
                for (const wallet of model.wallets.value) {
                    this.externalWallets.push(this.myWalletsBox.addWallet({
                        type: wallet.type === "COINBASE" ? MyWalletsBoxElementType.COINBASE : MyWalletsBoxElementType.METAMASK,
                        walletAddress: wallet.walletAddress
                    }));
                }
                if (this.externalWallets.length < 4) {
                    this.connectWallet = this.myWalletsBox.addWallet({ type: MyWalletsBoxElementType.CONNECT_WALLET });
                }
                this.layoutWalletsBox();
                if (oldExternalWalletsAddresses.length < this.externalWallets.length) {
                    this.myWalletsBox.currentWallet = this.externalWallets.find((wallet) => oldExternalWalletsAddresses.find((oldAddress) => oldAddress == wallet.data.walletAddress) == null);
                } else {
                    this.myWalletsBox.currentWallet = this.externalWallets.length > 0 ? this.externalWallets[this.externalWallets.length - 1] : this.mainWallet;
                }
                break;
        }
    }

    private async onWeb3ModelPropertyChanged(model: Web3Model, key: string) {
        switch (key) {
            case nameof(model, "address"):
                this.mainWallet.data.walletAddress = model.address.value;
                break;
            case nameof(model, "chainName"):
                this.mainWallet.data.networkType = model.chainName.value;
                break;
        }
    }

    private async onCloseButtonClickHandle(): Promise<void> {
        const inputBlocker = new FullscreenInputBlocker(this.canvasScaler);
        this.addChild(inputBlocker);
        await this.hide();
        this.removeChild(inputBlocker);
        this.operationControls.complete();
    }

    private onWalletCloseButtonClickHandle(): void {
        this.currentPage = this.scrollPage;
    }

    private onInfoViewClickHandle(): void {
        this.myWalletController.openExportPrivateKeyPage();
    }

    private async showFullBuilding(building: NFTBuilding): Promise<void> {
        this.fullBuildingContainer.visible = true;
        this.fullBuildingContainer.update(building);
        this.fullBuildingContainer.show();
    }

    private async show(): Promise<void> {
        this.largeParticles.play();
        this.smallParticles.play();
        await Promise.all([
            this.animations.play(),
            this.currentPage.show(MyWalletPopupView.ANIMATION_DURATION * 1.2)
        ]);
    }

    private async hide(): Promise<void> {
        await Promise.all([
            this.animations.reverse(),
            this.currentPage.hide(MyWalletPopupView.ANIMATION_DURATION * 0.5)
        ]);
    }

    public async start(): Promise<void> {
        this.fullBuildingContainer.visible = false;
        const inputBlocker = new FullscreenInputBlocker(this.canvasScaler);
        this.addChild(inputBlocker);
        await this.show();
        this.removeChild(inputBlocker);
    }

    public override destroy(options?: boolean | IDestroyOptions): void {
        this.myWalletsBox.onWalletClick.remove(this.onWalletClickHandle, this);
        this.closeButton.onUp.remove(this.onCloseButtonClickHandle, this);
        this.myWalletModel.propertyChanged.remove(this.onMyWalletModelPropertyChanged, this);
        this.web3Model.propertyChanged.remove(this.onWeb3ModelPropertyChanged, this);
        this.walletPage.onCloseButtonClick.remove(this.onWalletCloseButtonClickHandle, this);
        this.infoText.onContactClick.remove(this.onInfoViewClickHandle, this);
        this.walletsClickableArea.onClick.remove(this.myWalletsBox.showTooltip, this.myWalletsBox);
        this.scrollPage.onBuildingClick.remove(this.showFullBuilding, this);
        this.largeParticles.destroy();
        this.smallParticles.destroy();
        this.animations.kill();
        super.destroy(options);
    }
}
